import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useEffect, useMemo, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import InfiniteScroll from "react-swr-infinite-scroll";

import classNames from "classnames";
import { uniqBy } from "lodash";
import { Insight } from "models/insight";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import { collectionUrl } from "components/CollectionPageLayout";
import CustomCard from "components/CustomCard";
import { Loader } from "components/customElements/loader";
import DianomiAd from "components/DianomiAd";
import EmptyState from "components/EmptyState";
import FocusedSwitch from "components/FocusedSwitch";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import {
  InsightsListFilters,
  useInsightsFilters,
} from "components/InsightsListFilters";
import Paginator from "components/Paginator";
import PublicationSnippet from "components/PublicationSnippet";
import TopPickSwitch from "components/TopPickSwitch";
import ViewModeToggle from "components/ViewModeToggle";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import { INSIGHT_TYPES } from "consts/insightTypes";

import styles from "./style.module.scss";

type CollectionPageInsightsListProps = {};

export const collectionInsightsUrl = (
  slug: string,
  pageIndex: number,
  filters?: {
    text?: string;
    topPicksOnly?: boolean;
    focusedOnly?: boolean;
    insightTypes?: string[];
    geographies?: string[];
    countries?: string[];
    sectors?: string[];
    verticals?: string[];
    imperatives?: string[];
    timeHorizons?: string[];
    publishedDates?: { from?: Date; to?: Date };
  },
  sortStr?: string,
  mobile?: boolean,
  authenticated?: boolean
) => {
  const url = new ApiEndpoint(
    `/api/v2/collections/${slug}/${
      filters?.focusedOnly ? "focused-insights" : "insights"
    }`
  );
  url.searchParams.append(
    "include",
    "content-vertical,account,company,primary-entity,locker-items,smart-summary"
  );
  url.searchParams.append("filter[aasm-state]", "published");
  url.searchParams.append("page[size]", authenticated ? "12" : "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", sortStr || "-score,-published-at");
  url.searchParams.append(
    "fields[insights]",
    "content-vertical,account,company,primary-entity,locker-items,smart-summary" +
      ",slug,tagline,shareable-links,tagline," +
      (mobile ? "" : "byline,executive-summary,boomerang-reason,") +
      "total-combined-read,is-exchange-sponsored,is-broker,is-commissioned,is-boost,is-buyside,is-issuerpaid,is-syndicated,is-podcast," +
      "imperative,published-at,republished-at,conviction,is-embargoed," +
      "my-like-score,is-read"
  );
  url.searchParams.append("fields[accounts]", "slug,pretty-name");
  url.searchParams.append(
    "fields[accounts]",
    "slug,name,short-name,avatar-url,title,company-name,summary,areas-of-expertise,is-followed,is-insight-provider,activated"
  );
  url.searchParams.append(
    "fields[companies]",
    "slug,name,avatar-url,publish-under-company-name,title,analysts-count,summary,areas-of-expertise,is-followed"
  );
  url.searchParams.append("fields[content-verticals]", "slug,name");

  if (filters?.text) {
    url.searchParams.append("filter[fulltext]", filters?.text);
  }
  if (filters?.insightTypes?.length) {
    INSIGHT_TYPES.filter((type) => !!type.filterKey).forEach((type) => {
      if (filters?.insightTypes?.includes(type.key)) {
        url.searchParams.append(`filter[${type.filterKey}]`, "false");
      }
    });
    if (filters?.insightTypes?.includes("isInsight")) {
      INSIGHT_TYPES.filter((type) => !!type.filterKey).forEach((type) => {
        if (!filters?.insightTypes?.includes(type.key)) {
          url.searchParams.append(`filter[or][${type.filterKey}]`, "true");
        }
      });
    }
  }
  if (filters?.geographies?.length) {
    url.searchParams.append(
      "filter[geographies]",
      filters?.geographies?.map?.((g: any) => g.name)?.join(",")
    );
  }
  if (filters?.countries?.length) {
    url.searchParams.append(
      "filter[countries]",
      filters?.countries?.map?.((c: any) => c.name)?.join(",")
    );
  }
  if (filters?.sectors?.length) {
    url.searchParams.append(
      "filter[sectors]",
      filters?.sectors?.map?.((s: any) => s.name)?.join(",")
    );
  }
  if (filters?.verticals?.length) {
    url.searchParams.append(
      "filter[vertical-ids]",
      filters?.verticals?.map?.((v: any) => v.id)?.join(",")
    );
  }
  if (filters?.imperatives?.length) {
    url.searchParams.append(
      "filter[imperative]",
      filters?.imperatives?.join(",")
    );
  }
  if (filters?.timeHorizons?.length) {
    url.searchParams.append(
      "filter[time-horizon]",
      filters?.timeHorizons?.join(",")
    );
  }
  if (filters?.publishedDates?.from || filters?.publishedDates?.to) {
    url.searchParams.append(
      "filter[time-scope]",
      [
        filters?.publishedDates?.from || "2014-08-31T17:00:00.000Z",
        filters?.publishedDates?.to,
      ]
        .filter((t) => t)
        .join(",")
    );
  }
  if (filters?.topPicksOnly) {
    url.searchParams.append("filter[conviction]", "1");
  }

  return url.href;
};

export const sortOptions = [
  { id: "recent", name: "Recent", sortStr: "-published-at,-updated-at" },
  {
    id: "viewed",
    name: "Viewed",
    sortStr: "-total-combined-read,-published-at",
  },
  {
    id: "discussed",
    name: "Discussed",
    sortStr: "-total-discussions,-published-at",
  },
  {
    id: "liked",
    name: "Liked",
    sortStr: "-likes-count",
  },
];

const CollectionPageInsightsList = ({}: CollectionPageInsightsListProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const page = router.query.page && parseInt(router.query.page as string);

  const { mobile } = useMediaQuery();

  const { authenticated, account: loggedInAccount } = useAuthentication();

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  const [focusedOnly, setFocusedOnly] = useState(false);
  const [topPicksOnly, setTopPicksOnly] = useState(false);
  const [isCompact, setCompactView] = useState(mobile);
  const [sortOption, setSortOption] = useState(sortOptions[0]);

  const { data: collection } = useApi(collectionUrl(slug));

  const { InsightsSelectedFilters, filtersProps, selectedFilters } =
    useInsightsFilters();

  const filters = {
    ...selectedFilters,
    text: filterTxt,
    topPicksOnly,
    focusedOnly,
  };

  const swr = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return collectionInsightsUrl(
        slug,
        page || pageIndex + 1,
        filters,
        sortOption.sortStr,
        mobile && isCompact,
        authenticated
      );
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );
  const { data, isValidating, mutate, size, setSize } = swr;

  const totalPages = data?.[size - 1]?.meta?.totalPages;
  const hasMore = !page && totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;
  const isRefreshing = isValidating && data && data.length === size;

  const publications = useMemo(() => {
    const items = swr.data?.reduce((acc, page) => {
      if (page?.data) return [...acc, ...page.data];
      return acc;
    }, []);

    return uniqBy(items, (publication: Insight) => publication.id);
  }, [swr.data]);

  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (page && totalPages && page > totalPages) {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          page: totalPages,
        },
      });
    }
  }, [page, router, totalPages]);

  const firstPublication = data?.[0]?.data?.[0];
  const description = `${
    collection?.title ? `${collection?.title}. ` : ""
  }Latest Insight - ${firstPublication?.tagline}. View more on Smartkarma.`;

  const origin = useMemo(() => {
    const endpoint = `collections/${slug}/insights`;
    let sort = sortOption || sortOptions?.[0];
    const filters: { [key: string]: string } = {};

    if (filterTxt) {
      filters["filter[fulltext]"] = filterTxt;
    }

    if (selectedFilters?.geographies?.length) {
      filters["filter[geographies]"] = selectedFilters?.geographies
        ?.map?.((g: any) => g.name)
        ?.join(",");
    }
    if (selectedFilters?.countries?.length) {
      filters["filter[countries]"] = selectedFilters?.countries
        ?.map?.((c: any) => c.name)
        ?.join(",");
    }
    if (selectedFilters?.sectors?.length) {
      filters["filter[sectors]"] = selectedFilters?.sectors
        ?.map?.((s: any) => s.name)
        ?.join(",");
    }
    if (selectedFilters?.verticals?.length) {
      filters["filter[vertical-ids]"] = selectedFilters?.verticals
        ?.map?.((v: any) => v.id)
        ?.join(",");
    }
    if (selectedFilters?.imperatives?.length) {
      filters["filter[imperative]"] = selectedFilters?.imperatives?.join(",");
    }
    if (selectedFilters?.timeHorizons?.length) {
      filters["filter[time-horizon]"] =
        selectedFilters?.timeHorizons?.join(",");
    }
    if (
      selectedFilters?.publishedDates?.from ||
      selectedFilters?.publishedDates?.to
    ) {
      filters["filter[time-scope]"] = [
        selectedFilters?.publishedDates?.from || "2014-08-31T17:00:00.000Z",
        selectedFilters?.publishedDates?.to,
      ]
        .filter((t) => t)
        .join(",");
    }
    if (topPicksOnly) {
      filters["filter[conviction]"] = "1";
    }

    return {
      endpoint,
      sort,
      filters,
      page: size,
      pageSize: 5,
    };
  }, [
    filterTxt,
    selectedFilters?.countries,
    selectedFilters?.geographies,
    selectedFilters?.imperatives,
    selectedFilters?.publishedDates?.from,
    selectedFilters?.publishedDates?.to,
    selectedFilters?.sectors,
    selectedFilters?.timeHorizons,
    selectedFilters?.verticals,
    size,
    slug,
    sortOption,
    topPicksOnly,
  ]);

  const viewModeToggle = (
    <ViewModeToggle
      onToggle={setCompactView}
      isCompact={isCompact}
      showPager
      setSize={setSize}
    />
  );

  const snippetStyle = !mobile ? "snippetV2" : "snippet";

  return (
    <>
      <div className="mb-[15px]">
        <InsightsListFilters
          {...filtersProps}
          overrideEndpoints={{
            countries: `/api/v2/collections/${slug}/countries?sort=name`,
            sectors: `/api/v2/collections/${slug}/sectors?sort=name`,
            verticals: `/api/v2/collections/${slug}/content-verticals?sort=name`,
          }}
          showMoreFilters
        />
      </div>
      <CustomCard
        className={classNames(
          "p-0",
          "mx-0 rounded-none",
          "md:rounded-xl",
          "min-h-[max(calc(100vh-500px),720px)]"
        )}
      >
        <div className={styles.container}>
          <Head>
            <meta name="description" content={description} />
            <meta property="og:description" content={description} />
          </Head>
          <InsightsSelectedFilters />
          <div
            className={classNames(
              styles.filter,
              "flex flex-col-reverse space-y-reverse space-y-[15px] md:flex-row md:space-y-0",
              "p-[15px] md:px-[30px]",
              "mb-[10px] md:mb-0"
            )}
          >
            <div className="flex-1 w-full flex">
              <div
                className={classNames(
                  styles.filterBox,
                  "flex-1 flex pb-[10px] mr-[15px] md:pb-0 md:mr-0"
                )}
              >
                <FontAwesomeIcon
                  icon={faSearch}
                  className="top-[10px] md:top-[5px]"
                />
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
                  }
                >
                  <input
                    className="md:max-w-[200px]"
                    value={filterTxtValue}
                    autoComplete="false"
                    autoCapitalize="false"
                    onChange={(e) => setFilterTxtValue(e.target.value)}
                    aria-label="filter"
                  />
                </OverlayTrigger>
              </div>
              {mobile && viewModeToggle}
            </div>
            <div className="flex w-full md:w-auto">
              <div className="mr-[30px]">
                <FocusedSwitch
                  checked={focusedOnly}
                  onSwitch={setFocusedOnly}
                  // tooltip={<div className="text-center">Text</div>}
                />
              </div>
              <div className="flex-1">
                <TopPickSwitch
                  checked={topPicksOnly}
                  onSwitch={setTopPicksOnly}
                />
              </div>
              <span
                className={classNames(
                  styles.dropdown,
                  "-mr-[10px] md:mr-[10px]"
                )}
              >
                <Dropdown>
                  <Dropdown.Toggle variant="success" id="dropdown-basic">
                    {sortOption.name} <FontAwesomeIcon icon={faCaretDown} />
                  </Dropdown.Toggle>

                  <Dropdown.Menu className={styles.menu}>
                    {sortOptions.map((item) => (
                      <Dropdown.Item
                        className={classNames(
                          styles.item,
                          sortOption.id === item.id && styles.active
                        )}
                        key={item.id}
                        onClick={() => setSortOption(item)}
                      >
                        {item.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </span>
            </div>
            {!mobile && viewModeToggle}
          </div>
          <div className={styles.list}>
            {isRefreshing ? (
              <Loader />
            ) : (
              <InfiniteScroll
                swr={{ data, mutate, isValidating, size, setSize }}
                isReachingEnd={!hasMore}
                endingIndicator={
                  isValidating ? (
                    <Loader />
                  ) : isEmpty ? (
                    <EmptyState
                      type={filterTxt ? "search" : "publication"}
                      query={filterTxt}
                      onActionClick={() =>
                        router.push(`/collections/${slug}/research`)
                      }
                      onClearClick={() => setFilterTxtValue("")}
                    />
                  ) : page ? (
                    <Paginator totalPages={totalPages} />
                  ) : (
                    <div className={styles.endOfList}>No more insights</div>
                  )
                }
              >
                <>
                  {publications?.map?.((publication: any, index: number) => {
                    if (index === 1) {
                      return (
                        <div key={publication.id} className="">
                          <PublicationSnippet
                            key={publication.id}
                            publication={publication}
                            isCompact={isCompact}
                            useModal={authenticated}
                            originCollection={collection}
                            origin={origin}
                            swr={swr}
                            style={snippetStyle}
                          />
                          <DianomiAd type="in_list_medium" />
                        </div>
                      );
                    }

                    if (index === 4) {
                      return (
                        <div key={publication.id} className="">
                          <PublicationSnippet
                            key={publication.id}
                            publication={publication}
                            isCompact={isCompact}
                            useModal={authenticated}
                            originCollection={collection}
                            origin={origin}
                            swr={swr}
                            style={snippetStyle}
                          />
                          <DianomiAd type="in_list" />
                        </div>
                      );
                    }

                    return (
                      <PublicationSnippet
                        key={publication.id}
                        publication={publication}
                        isCompact={isCompact}
                        useModal={authenticated}
                        originCollection={collection}
                        origin={origin}
                        swr={swr}
                        style={snippetStyle}
                      />
                    );
                  })}
                </>
              </InfiniteScroll>
            )}
          </div>
        </div>
      </CustomCard>
    </>
  );
};

export default CollectionPageInsightsList;
